import { Grid, useEventCallback } from '@mui/material';
import { memo, useState, useCallback, useMemo, Fragment, useEffect } from 'react';
import { PmiButton } from 'shell/components/shared/button';
import { __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprData } from 'shell/models/person';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import {
  Parenthood,
  ParenthoodSearchForm,
  SubscriptionAccordionGenerality,
  CommonInterfaceSubscription,
} from 'subscription/view-models/common-interface-subscription';
import FormGenerality from '../generality/form';
import { transformerParenthoodData } from 'subscription/utils/transformer-parenthood';
import { FormikErrors, FormikTouched } from 'formik';
import { AnprVM } from 'shell/view-models/search-anpr';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { PersonVM } from 'shell/view-models/search-person';
import { ItemVM } from 'shell/view-models/item';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import Table from 'shell/components/shared/table';
import { noop } from 'shell/utils/noop';
import { headCells } from 'front-office/components/shared/generality/form';
import AccordionFilterPerson from '../accordion-filter-person';
import { useCheckPraticheAperte } from 'subscription/hooks/useCheckPraticheAperte';

interface FatherhoodFormProps {
  disabled?: boolean;
  values?: CommonInterfaceSubscription['fatherhood'];
  errorsPerson?: FormikErrors<ParenthoodSearchForm>;
  touchedPerson?: FormikTouched<ParenthoodSearchForm>;
  errorsGenerality?: FormikErrors<ParenthoodSearchForm>;
  touchedGenerality?: FormikTouched<ParenthoodSearchForm>;
  onChangePerson: (
    key: keyof ParenthoodSearchForm,
    value: ParenthoodSearchForm[keyof ParenthoodSearchForm]
  ) => void;
  onChangesPerson: (values: ParenthoodSearchForm | null) => void;
  onChangeGenerality: (
    key: keyof SubscriptionAccordionGenerality,
    value: SubscriptionAccordionGenerality[keyof SubscriptionAccordionGenerality]
  ) => void;
  onChangeValuesGenerality: (values: SubscriptionAccordionGenerality) => void;
  resetForm: () => void;
  onChangeValues: (values?: Parenthood) => void;
  isCohabitation?: boolean;
}

const FatherhoodFormComponent = ({
  disabled,
  values,
  onChangePerson,
  onChangesPerson,
  onChangeGenerality,
  onChangeValuesGenerality,
  resetForm,
  onChangeValues,
  errorsGenerality,
  touchedGenerality,
  isCohabitation,
}: FatherhoodFormProps): React.ReactElement => {
  // accordion filter
  const [openAccordion, setOpenAccordion] = useState<boolean>(true);

  const { checkPratiche, errorModal } = useCheckPraticheAperte();

  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });
  const [visibilityTable, setVisibilityTable] = useState(false);

  const [paginationState, setPaginationState] = useState({
    page: 0,
    rowsPerPage: 10,
  });

  const [anpr, getAnpr] = useHttpFetch<
    HttpResponse<ResponseAnprData>,
    AnprVM<CommonInterfaceSubscription>
  >(`${__PERSON_NEW_API_URL__}/persona`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
    },
    transformer: (response) => transformerParenthoodData(response, isCohabitation),
  });

  const getPerson = useHttpClient<
    HttpResponse<ResponseAnprData>,
    AnprVM<CommonInterfaceSubscription>
  >(`${__PERSON_NEW_API_URL__}/persona`, {
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
    transformer: (response) => transformerParenthoodData(response, isCohabitation),
  });

  // Reset state pagination
  const resetPagination = useEventCallback(() => {
    setPaginationState({
      page: 0,
      rowsPerPage: 10,
    });
  });

  // Handle Search
  const handleSearch = useEventCallback(() => {
    setVisibilityTable(false);

    if (
      !values?.person?.fiscalCodeSearchPerson &&
      !(values?.person?.nameSearchPerson || values?.person?.surnameSearchPerson)
    ) {
      return;
    }

    Promise.all([
      checkPratiche(values?.person?.fiscalCodeSearchPerson),
      getAnpr({
        data: {
          criteriRicerca: {
            sesso: values?.person?.gender || undefined,
            codiceFiscale: values?.person?.fiscalCodeSearchPerson || undefined,
            cognome: values?.person?.surnameSearchPerson || undefined,
            senzaCognome:
              !values?.person?.surnameSearchPerson || values?.person?.withoutSurnameSearchPerson
                ? '1'
                : undefined,
            nome: values?.person?.nameSearchPerson || undefined,
            senzaNome:
              !values?.person.nameSearchPerson || values?.person?.withoutNameSearchPerson
                ? '1'
                : undefined,
          },
          datiNascita: values?.person.birthDate
            ? {
                senzaGiorno:
                  values?.person?.birthDate &&
                  !values?.person?.birthChoiceDate?.includes('day') &&
                  values?.person?.birthChoiceDate?.includes('month')
                    ? '1'
                    : undefined,
                senzaGiornoMese:
                  values?.person.birthDate &&
                  !values?.person?.birthChoiceDate?.includes('day') &&
                  !values?.person?.birthChoiceDate?.includes('month')
                    ? '1'
                    : undefined,
                dataEvento: values?.person.birthDate?.toFormat('yyyy-MM-dd') || undefined,
              }
            : undefined,
          datiRichiesta: {
            ricercaNazionale: '1',
          },
        },
      }) as ResponsePromise<HttpResponse<ResponseAnprData>, AnprVM<CommonInterfaceSubscription>>,
    ]).then(([, response]) => {
      const responseCasted = response as AnprVM<CommonInterfaceSubscription>;
      if (responseCasted?.persons?.length === 1) {
        const [element] = responseCasted?.persons || [];

        onChangeValues(element.fatherhood || undefined);
        setVisibilityTable(true);
        setOpenAccordion(false);
        return;
      }
      resetPagination();
      setOpenAccordion(false);
      setVisibilityTable(true);
    });
  });

  const castedAnprData = anpr.data as AnprVM<CommonInterfaceSubscription>;

  const onVisibility = useCallback(
    (open, type) => {
      const visibilityMessage = type === 'error' && !open ? undefined : open;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  // Handle change page
  const handleChangePage = useEventCallback((page: number) => {
    setPaginationState((oldState) => ({ ...oldState, page }));
  });

  // Handle change rows per page
  const handleChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    setPaginationState((oldState) => ({ ...oldState, page: 0, rowsPerPage }));
  });

  const handleClickSelect = useEventCallback((person: CommonInterfaceSubscription) => {
    (
      getPerson({
        data: {
          criteriRicerca: {
            codiceFiscale: person?.fatherhood?.person?.fiscalCode || undefined,
            idSchedaSoggettoANPR: person?.fatherhood?.generality?.anprIdentifier || undefined,
          },
          datiRichiesta: {
            ricercaNazionale: '0',
          },
        },
      }) as ResponsePromise<HttpResponse<ResponseAnprData>, AnprVM<CommonInterfaceSubscription>>
    ).then((response) => {
      const castedResponse = response as AnprVM<CommonInterfaceSubscription>;
      if (castedResponse?.persons?.length === 1) {
        const [element] = castedResponse?.persons || [];

        onChangeValues(element.fatherhood || undefined);

        setVisibilityTable(false);
      }
      resetPagination();
      setVisibility({});
    });
  });

  const dataPersons: Array<PersonVM> = useMemo(() => {
    return (
      castedAnprData?.persons?.map((person) => ({
        fiscalCode: person.fatherhood?.person?.fiscalCode || '',
        surname: person.fatherhood?.person?.surname || '',
        name: person.fatherhood?.person?.name || '',
        gender: person.fatherhood?.generality?.gender || '',
        birthday: person.fatherhood?.generality?.birthDate?.toFormat('dd/MM/yyyy') || '',
        birthplace: person.fatherhood?.birthPlace,
        residence: person.fatherhood?.residenceString,
        state: person.fatherhood?.state,
        anpr: person.fatherhood?.generality?.anprIdentifier,
        action: (
          <PmiButton
            variant="outlined"
            color="cdmBrandThird"
            onClick={() => handleClickSelect(person)}
          >
            Seleziona
          </PmiButton>
        ),
      })) || []
    );
  }, [castedAnprData?.persons, handleClickSelect]);

  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (castedAnprData?.errors?.length > 0 && visibility.error === undefined) {
      errorToVisualize.title =
        castedAnprData?.errors?.length > 1
          ? `Ci sono ${castedAnprData?.errors?.length} errori`
          : `C'è 1 errore`;
      // errorToVisualize.title = `Ci sono ${(anpr.data as AnprVM<PersonVM>)?.errors?.length} errori`;
      errorToVisualize.messages = castedAnprData?.errors;
      errorToVisualize.visibility = true;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, anpr.data]);

  useEffect(() => {
    if (disabled) {
      setOpenAccordion(false);
    }
  }, [disabled]);

  useEffect(() => {
    if (!values?.generality?.fiscalCode && disabled) {
      onChangesPerson({
        surname: values?.person?.surname,
        withoutSurname: !Boolean(values?.person?.surname),
        name: values?.person?.name,
        withoutName: !Boolean(values?.person?.name),
        gender: values?.generality?.gender,
        birthDate: values?.generality?.birthDate,
      });
    }
  }, [values?.generality]);

  return (
    <Fragment>
      {errorModal}
      {visibilityTable || errorMessage.visibility ? (
        <>
          <SearchMessages
            type={MessageType.Error}
            title={errorMessage.title || ''}
            messages={errorMessage.messages || []}
            visibility={errorMessage.visibility || false}
            onChangeVisibility={onVisibility}
          />
          <SearchMessages
            type={MessageType.Warning}
            title={
              castedAnprData?.warnings?.length > 1
                ? `Ci sono ${castedAnprData?.warnings?.length} warning`
                : `C'è 1 warning`
            }
            messages={castedAnprData?.warnings || []}
            visibility={
              visibility.warning !== undefined
                ? visibility.warning
                : (castedAnprData?.warnings || [])?.length > 0
            }
            onChangeVisibility={onVisibility}
          />
        </>
      ) : undefined}
      <Grid container item spacing={2}>
        <Grid item xs={12}>
          <AccordionFilterPerson
            onChange={onChangePerson}
            onChangeFields={onChangesPerson}
            onSearch={handleSearch}
            setOpen={setOpenAccordion}
            open={openAccordion}
            disabled={values?.person?.surname ? false : disabled}
            values={values?.person}
            resetForm={resetForm}
          />
        </Grid>
        {dataPersons.length > 1 && visibilityTable ? (
          <Grid item xs={12}>
            <Table
              ariaLabel="Lista Persone"
              page={paginationState.page}
              rowsPerPage={paginationState.rowsPerPage}
              body={dataPersons}
              count={dataPersons.length}
              head={headCells}
              onSort={noop}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              onVisibilityPress={noop}
              orders={{}}
              isNotLayoutFixed
              noSort
              hideButtons
            />
          </Grid>
        ) : undefined}
        <Grid item xs={12}>
          <FormGenerality
            onChangeGenerality={onChangeGenerality}
            isCohabitation={isCohabitation}
            generality={values?.generality}
            resetForm={resetForm}
            onChangeValues={onChangeValuesGenerality}
            errorsGenerality={errorsGenerality}
            touchedGenerality={touchedGenerality}
            disabled={disabled}
          />
        </Grid>
      </Grid>
    </Fragment>
  );
};

FatherhoodFormComponent.displayName = 'fatherhood form component';

const FatherhoodForm = memo(FatherhoodFormComponent);

export default FatherhoodForm;
